import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Camera, Check, Bell, BellOff, Search, Loader2, List, Sparkles } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';

export default function EditProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isFirstTime = location.state?.firstTime === true;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form fields
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [followedBrands, setFollowedBrands] = useState([]);
  const [brandNotifications, setBrandNotifications] = useState(true);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [allBrands, setAllBrands] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchUser();
    fetchBrands();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await authFetch(`${API}/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setName(userData.name || '');
        setUsername(userData.username || '');
        setBio(userData.bio || '');
        setFollowedBrands(userData.followed_brands || []);
        setBrandNotifications(userData.brand_notifications !== false);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await authFetch(`${API}/brands`);
      if (response.ok) {
        const brands = await response.json();
        setAllBrands(brands);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authFetch(`${API}/users/profile/picture`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setUser(prev => ({ ...prev, picture: data.picture }));
        toast.success('Profile picture updated!');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to upload picture');
      }
    } catch (error) {
      console.error('Error uploading picture:', error);
      toast.error('Failed to upload picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const toggleBrand = (brandId) => {
    setFollowedBrands(prev => 
      prev.includes(brandId)
        ? prev.filter(b => b !== brandId)
        : [...prev, brandId]
    );
  };

  const [showChecklistNudge, setShowChecklistNudge] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!username.trim()) {
      toast.error('Username is required');
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`${API}/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          username: username.toLowerCase().replace(/\s+/g, ''),
          bio,
          followed_brands: followedBrands,
          brand_notifications: brandNotifications
        })
      });

      if (response.ok) {
        if (isFirstTime) {
          toast.success('Profile created! Welcome to Gloverse!');
          setShowChecklistNudge(true);
        } else {
          toast.success('Profile updated!');
          navigate(-1);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          {isFirstTime ? (
            <div className="w-10" />
          ) : (
            <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isFirstTime ? 'Set Up Your Profile' : 'Edit Profile'}
          </h1>
          <button 
            data-testid="save-profile-btn"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary px-6 py-2 text-sm disabled:opacity-50"
          >
            {saving ? 'Saving...' : isFirstTime ? 'Get Started' : 'Save'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {isFirstTime && (
          <div className="text-center py-2">
            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Welcome to Gloverse!</h2>
            <p className="text-muted-foreground">Let's set up your profile so others can find you</p>
          </div>
        )}
        {/* Profile Picture */}
        <div className="flex justify-center">
          <div className="relative cursor-pointer group" onClick={() => !uploadingPicture && fileInputRef.current?.click()}>
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full border-4 border-primary/20 object-cover group-hover:opacity-80 transition-opacity" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-3xl font-bold group-hover:opacity-80 transition-opacity">
                {name?.charAt(0) || 'U'}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePictureUpload}
              data-testid="profile-picture-input"
            />
            <div
              data-testid="upload-picture-btn"
              className="absolute bottom-0 right-0 p-2 bg-primary text-white rounded-full shadow-lg group-hover:bg-primary/90 transition-colors"
            >
              {uploadingPicture ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-2xl p-6 border border-border space-y-4">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Basic Info</h2>
          
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Name <span className="text-red-500">*</span></label>
            <input
              data-testid="name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              className={`w-full px-4 py-3 rounded-xl border focus:border-primary outline-none ${!name.trim() && isFirstTime ? 'border-red-300 bg-red-50/30' : 'border-border'}`}
            />
            {!name.trim() && isFirstTime && (
              <p className="text-xs text-red-500 mt-1">Name is required to continue</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Username</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
              <input
                data-testid="username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                className="w-full pl-8 pr-4 py-3 rounded-xl border border-border focus:border-primary outline-none"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Bio</label>
            <textarea
              data-testid="bio-input"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell us about yourself..."
              className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none resize-none"
              rows={3}
              maxLength={150}
            />
            <p className="text-xs text-muted-foreground text-right mt-1">{bio.length}/150</p>
          </div>
        </div>

        {/* Brand Notifications Toggle */}
        <div className="bg-white rounded-2xl p-6 border border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {brandNotifications ? (
                <Bell className="w-5 h-5 text-primary" />
              ) : (
                <BellOff className="w-5 h-5 text-muted-foreground" />
              )}
              <div>
                <p className="font-medium">Brand Notifications</p>
                <p className="text-sm text-muted-foreground">Get notified about new products from followed brands</p>
              </div>
            </div>
            <button
              data-testid="toggle-brand-notifications"
              onClick={() => setBrandNotifications(!brandNotifications)}
              className={`relative w-14 h-8 rounded-full transition-colors ${
                brandNotifications ? 'bg-primary' : 'bg-border'
              }`}
            >
              <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                brandNotifications ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>

        {/* Follow Brands */}
        <div className="bg-white rounded-2xl p-6 border border-border">
          <h2 className="text-lg font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Follow Your Favorite Brands</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Get updates on new products, exclusive offers, and see stats about these brands
          </p>
          
          {allBrands.length > 10 && (
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                data-testid="brand-search-input"
                type="text"
                value={brandSearch}
                onChange={(e) => setBrandSearch(e.target.value)}
                placeholder="Search brands..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-white/50 focus:border-primary outline-none text-sm placeholder:text-muted-foreground/40"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1">
            {allBrands
              .filter(b => !brandSearch || (typeof b === 'string' ? b : b.name).toLowerCase().includes(brandSearch.toLowerCase()))
              .map(brandItem => {
                const brand = typeof brandItem === 'string' ? brandItem : brandItem.name;
                const brandId = brand.toLowerCase().replace(/\s+/g, '_').replace(/'/g, '');
                const isFollowed = followedBrands.includes(brandId);
                return (
                  <button
                    key={brandId}
                    data-testid={`brand-${brandId}`}
                    onClick={() => toggleBrand(brandId)}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border transition-all text-left ${
                      isFollowed
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <span className="text-sm font-medium flex-1 truncate">{brand}</span>
                    {isFollowed && <Check className="w-4 h-4 shrink-0" />}
                  </button>
                );
              })}
          </div>
          
          <p className="text-xs text-muted-foreground mt-4 text-center">
            Following {followedBrands.length} brand{followedBrands.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Checklist Nudge Modal */}
      {showChecklistNudge && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="checklist-nudge-modal">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl text-center space-y-5 animate-in fade-in zoom-in-95 duration-300">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent mx-auto flex items-center justify-center">
              <List className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Create your first checklist!
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Share your beauty routine with the community. Pick 3-7 products you love and create your signature checklist.
              </p>
            </div>
            <div className="space-y-3">
              <button
                data-testid="nudge-create-checklist-btn"
                onClick={() => navigate('/create-checklist', { replace: true })}
                className="w-full btn-primary py-3 text-base font-semibold flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Create My Checklist
              </button>
              <button
                data-testid="nudge-skip-btn"
                onClick={() => { setShowChecklistNudge(false); navigate('/', { replace: true }); }}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
              >
                I'll do it later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
